import type {WorkflowResult} from '@linx-security/api-specs';
import {WorkflowResultType} from '@linx-security/api-specs';
import type {ApiResponse} from '@linx-security/api';

import type {createBackgroundTasksStore} from './store';

export type TaskToastRetryButton = {
  children?: string;
  onClick?: () => void;
  href?: string;
  [key: string]: unknown;
};

export type TaskToastRetry = ((status: string) => TaskToastRetryButton | null) | TaskToastRetryButton | null;

export type TaskStatus = WorkflowResultType;
export const TaskStatus = WorkflowResultType;
// Rename for this type to not be confused with external types
export type BackgroundTaskResult<Type extends TaskStatus = TaskStatus> = Omit<WorkflowResult, 'type'> & {type: Type};

export type Task = BackgroundTaskResult & {
  isFetching?: boolean;
  isNotified?: boolean;
  /** When true, the global notification hook will skip toasts for this task. Use when the consumer handles its own notifications. */
  silent?: boolean;
  interval?: number;
  toast?: {
    id?: string;
    success?: string;
    error?: string;
    loading?: string;
    retry?: TaskToastRetry;
  };
};

export type SuccessfulDownloadResult = BackgroundTaskResult<'SUCCESS'> & {
  payload: {
    url: string;
  };
};

export type Dependencies = {
  /**
   * A function that subscribes to a background task
   */
  subscribe: (taskId: string) => Promise<ApiResponse<{id: string; result: WorkflowResult}>>;
};

export type State = {
  tasks: Map<string, Task>;
};

export type Actions = {
  // External actions
  updateTask: (id: string, task: Task) => void;
  addTask: (id: string, task: Omit<Task, 'isNotified' | 'isFetching'>) => Task;
  removeTask: (id: string) => void;
  // Internal actions
  _onTaskSuccess: (taskId: string, payload?: Partial<BackgroundTaskResult<'SUCCESS'>> & Task) => void;
  _onTaskFailure: (taskId: string, payload?: Partial<BackgroundTaskResult<'FAILURE'>> & Task) => void;
  _onTaskPending: (taskId: string, payload?: Partial<BackgroundTaskResult<'PENDING'>> & Omit<Task, 'type'>) => void;
};

export type BackgroundTasksStore = State & Actions;
export type BackgroundTasksStoreApi = ReturnType<typeof createBackgroundTasksStore>;
